import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, DialogContent, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useId } from "react";
import type { FormikErrors, FormikTouched } from "formik";
import type { Usuario } from "../../firebase/firestore/usuarios";
import useUserFormik from "../../hooks/useUserHook";
import useBreakpoint from "../../utils/useBreakpoint";
import InputFormik from "../inputs/InputFormik";
import SelectCountryCodes from "../inputs/SelectCountryCodes";
import UserAutocomplete from "../inputs/UserAutocomplete";
import type Fee from "../../interfaces/Fee";
import type { Referente } from "../../firebase/firestore/usuarios";
import UserRole from "../../interfaces/UserRole";

type FormValues = {
    tarifaAerea?: Fee;
    tarifaMaritima?: Fee;
    [key: string]: unknown;
};


export default function FormUser({
    entity,
    onClose,
}: {
    entity: Usuario;
    onClose: () => void;
}) {
    const bp = useBreakpoint();
    const currencyAereaId = useId();
    const currencyMaritimaId = useId();
    const roleId = useId();
    const estadoId = useId();
    const {
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        handleSubmit,
    } = useUserFormik(entity);

    // Helper para acceder a errores anidados de forma segura
    const getNestedError = (
        errors: FormikErrors<FormValues>,
        path: "tarifaAerea" | "tarifaMaritima",
        field: keyof Fee
    ): string => {
        const nestedError = errors[path];
        if (nestedError && typeof nestedError === "object" && field in nestedError) {
            const errorValue = nestedError[field as keyof typeof nestedError];
            return typeof errorValue === "string" ? errorValue : "";
        }
        return "";
    };

    // Helper para acceder a touched anidados de forma segura
    const getNestedTouched = (
        touched: FormikTouched<FormValues>,
        path: "tarifaAerea" | "tarifaMaritima",
        field: keyof Fee
    ): boolean => {
        const nestedTouched = touched[path];
        if (nestedTouched && typeof nestedTouched === "object" && field in nestedTouched) {
            return !!nestedTouched[field as keyof typeof nestedTouched];
        }
        return false;
    };

    const handleSubmitAndCloseModal = () => {
        handleSubmit();
        onClose();
    }

    return (
        <DialogContent sx={{ maxWidth: "100%" }}>
            <Grid container spacing={4} sx={{ paddingX: 4, paddingTop: 2 }}>
                <Grid size={{ sm: 4, md: 6 }}>
                    <InputFormik
                        label="Nombre"
                        name="name"
                        value={values.name}
                        error={!!touched.name && !!errors.name}
                        errorText={errors.name ?? ""}
                        handleChange={handleChange("name")}
                        handleBlur={handleBlur("name")}
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <InputFormik
                        label="Apellido"
                        name="lastName"
                        value={values.lastName}
                        error={!!touched.lastName && !!errors.lastName}
                        errorText={errors.lastName ?? ""}
                        handleChange={handleChange("lastName")}
                        handleBlur={handleBlur("lastName")}
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <InputFormik
                        label="Correo"
                        name="email"
                        value={values.email}
                        error={!!touched.email && !!errors.email}
                        errorText={errors.email ?? ""}
                        handleChange={handleChange("email")}
                        handleBlur={handleBlur("email")}
                        type="email"
                        autoCapitalize="none"
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <Stack
                        sx={{
                            flexDirection: "row",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <SelectCountryCodes
                            label={"Cód."}
                            name={"countryCode"}
                            value={values.countryCode}
                            error={!!touched.countryCode && !!errors.countryCode}
                            errorText={errors.countryCode ?? ""}
                            setFieldValue={setFieldValue}
                            handleBlur={handleBlur("countryCode")}
                        />

                        <InputFormik
                            label="Teléfono"
                            name="phone"
                            value={values.phone}
                            error={!!touched.phone && !!errors.phone}
                            errorText={errors.phone ?? ""}
                            handleChange={handleChange("phone")}
                            handleBlur={handleBlur("phone")}
                            style={{ width: bp === "xs" || bp === "sm" ? "60%" : "70%" }}
                        />
                    </Stack>
                </Grid>
                <Grid size={{ sm: 4, md: 6 }}>
                    <InputFormik
                        label="Cliente"
                        name="lockerCode"
                        value={values.lockerCode}
                        error={!!touched.lockerCode && !!errors.lockerCode}
                        errorText={errors.lockerCode ?? ""}
                        handleChange={handleChange("lockerCode")}
                        handleBlur={handleBlur("lockerCode")}
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <FormControl fullWidth error={!!touched.role && !!errors.role}>
                        <InputLabel id={roleId}>Rol</InputLabel>
                        <Select
                            labelId={roleId}
                            id={roleId}
                            value={values.role ?? ""}
                            label="Rol"
                            onChange={(e) => {
                                const value = e.target.value;
                                setFieldValue("role", value ? (value as UserRole) : undefined);
                            }}
                            onBlur={handleBlur("role")}
                            size="small"
                        >
                            <MenuItem value={UserRole.Admin}>Admin</MenuItem>
                            <MenuItem value={UserRole.Digitador}>Digitador</MenuItem>
                            <MenuItem value={UserRole.Cliente}>Cliente</MenuItem>
                        </Select>
                        {touched.role && errors.role && (
                            <Box sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5, ml: 1.75 }}>
                                {errors.role}
                            </Box>
                        )}
                    </FormControl>
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <InputFormik
                        label="Puntos"
                        name="puntos"
                        type="number"
                        value={values.puntos !== undefined && values.puntos !== null ? String(values.puntos) : ""}
                        error={!!touched.puntos && !!errors.puntos}
                        errorText={errors.puntos ?? ""}
                        handleChange={(e) => {
                            const value = e.target.value === "" ? undefined : Number(e.target.value);
                            setFieldValue("puntos", value);
                        }}
                        handleBlur={handleBlur("puntos")}
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <FormControl fullWidth>
                        <InputLabel id={estadoId}>Estado</InputLabel>
                        <Select
                            labelId={estadoId}
                            id={estadoId}
                            value={values.estado ?? ""}
                            label="Estado"
                            onChange={(e) => {
                                const value = e.target.value;
                                setFieldValue("estado", value || undefined);
                            }}
                            size="small"
                        >
                            <MenuItem value="Pendiente">Pendiente</MenuItem>
                            <MenuItem value="Confirmado">Confirmado</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <UserAutocomplete
                        label="Vendedor"
                        value={values.vendedor as Referente | undefined}
                        excludeId={values.id as string}
                        onChange={(ref) => setFieldValue("vendedor", ref)}
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 6 }}>
                    <UserAutocomplete
                        label="Referido por"
                        value={values.referidoPor as Referente | undefined}
                        excludeId={values.id as string}
                        onChange={(ref) => setFieldValue("referidoPor", ref)}
                    />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>
                        Tarifas Personalizadas
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Tarifa Aérea
                        </Typography>
                        {(values.tarifaAerea?.nombre || values.tarifaAerea?.monto !== undefined || values.tarifaAerea?.moneda) && (
                            <IconButton
                                size="small"
                                onClick={() => setFieldValue("tarifaAerea", undefined)}
                                color="error"
                                title="Eliminar tarifa aérea"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Grid>

                <Grid size={{ sm: 4, md: 4 }}>
                    <InputFormik
                        label="Nombre"
                        name="tarifaAerea.nombre"
                        value={values.tarifaAerea?.nombre ?? ""}
                        error={getNestedTouched(touched, "tarifaAerea", "nombre") && !!getNestedError(errors, "tarifaAerea", "nombre")}
                        errorText={getNestedError(errors, "tarifaAerea", "nombre")}
                        handleChange={(e) => {
                            const currentTarifa = values.tarifaAerea || {};
                            setFieldValue("tarifaAerea", {
                                ...currentTarifa,
                                nombre: e.target.value,
                            });
                        }}
                        handleBlur={handleBlur("tarifaAerea.nombre")}
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 4 }}>
                    <InputFormik
                        label="Monto"
                        name="tarifaAerea.monto"
                        type="number"
                        value={values.tarifaAerea?.monto !== undefined ? String(values.tarifaAerea.monto) : ""}
                        error={getNestedTouched(touched, "tarifaAerea", "monto") && !!getNestedError(errors, "tarifaAerea", "monto")}
                        errorText={getNestedError(errors, "tarifaAerea", "monto")}
                        handleChange={(e) => {
                            const value = e.target.value === "" ? undefined : Number(e.target.value);
                            const currentTarifa = values.tarifaAerea || {};
                            setFieldValue("tarifaAerea", {
                                ...currentTarifa,
                                monto: value,
                            });
                        }}
                        handleBlur={handleBlur("tarifaAerea.monto")}
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 4 }}>
                    <FormControl fullWidth>
                        <InputLabel id={currencyAereaId}>Moneda</InputLabel>
                        <Select
                            labelId={currencyAereaId}
                            id={currencyAereaId}
                            value={values.tarifaAerea?.moneda ?? ""}
                            label="Moneda"
                            onChange={(e) => {
                                const currentTarifa = values.tarifaAerea || {};
                                setFieldValue("tarifaAerea", {
                                    ...currentTarifa,
                                    moneda: e.target.value || undefined,
                                });
                            }}
                            size="small"
                        >
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="C$">C$</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Tarifa Marítima
                        </Typography>
                        {(values.tarifaMaritima?.nombre || values.tarifaMaritima?.monto !== undefined || values.tarifaMaritima?.moneda) && (
                            <IconButton
                                size="small"
                                onClick={() => setFieldValue("tarifaMaritima", undefined)}
                                color="error"
                                title="Eliminar tarifa marítima"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Grid>

                <Grid size={{ sm: 4, md: 4 }}>
                    <InputFormik
                        label="Nombre"
                        name="tarifaMaritima.nombre"
                        value={values.tarifaMaritima?.nombre ?? ""}
                        error={getNestedTouched(touched, "tarifaMaritima", "nombre") && !!getNestedError(errors, "tarifaMaritima", "nombre")}
                        errorText={getNestedError(errors, "tarifaMaritima", "nombre")}
                        handleChange={(e) => {
                            const currentTarifa = values.tarifaMaritima || {};
                            setFieldValue("tarifaMaritima", {
                                ...currentTarifa,
                                nombre: e.target.value,
                            });
                        }}
                        handleBlur={handleBlur("tarifaMaritima.nombre")}
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 4 }}>
                    <InputFormik
                        label="Monto"
                        name="tarifaMaritima.monto"
                        type="number"
                        value={values.tarifaMaritima?.monto !== undefined ? String(values.tarifaMaritima.monto) : ""}
                        error={getNestedTouched(touched, "tarifaMaritima", "monto") && !!getNestedError(errors, "tarifaMaritima", "monto")}
                        errorText={getNestedError(errors, "tarifaMaritima", "monto")}
                        handleChange={(e) => {
                            const value = e.target.value === "" ? undefined : Number(e.target.value);
                            const currentTarifa = values.tarifaMaritima || {};
                            setFieldValue("tarifaMaritima", {
                                ...currentTarifa,
                                monto: value,
                            });
                        }}
                        handleBlur={handleBlur("tarifaMaritima.monto")}
                    />
                </Grid>

                <Grid size={{ sm: 4, md: 4 }}>
                    <FormControl fullWidth>
                        <InputLabel id={currencyMaritimaId}>Moneda</InputLabel>
                        <Select
                            labelId={currencyMaritimaId}
                            id={currencyMaritimaId}
                            value={values.tarifaMaritima?.moneda ?? ""}
                            label="Moneda"
                            onChange={(e) => {
                                const currentTarifa = values.tarifaMaritima || {};
                                setFieldValue("tarifaMaritima", {
                                    ...currentTarifa,
                                    moneda: e.target.value || undefined,
                                });
                            }}
                            size="small"
                        >
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="C$">C$</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }} sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                    <Button onClick={onClose} sx={{ mr: 1 }}>
                        Cancelar
                    </Button>
                    <Box sx={{ flex: "1 1 auto" }} />
                    <Button onClick={handleSubmitAndCloseModal}>
                        Guardar
                    </Button>
                </Grid>
            </Grid>
        </DialogContent>
    )
}